import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/common/Pagination';
import { TableZoomControls } from '@/components/common/TableZoomControls';
import { useTableZoom } from '@/lib/hooks/useTableZoom';
import { useAuthStore } from '@/features/auth/stores/auth.store';

import { ClientsDataTable } from '../components/ClientsDataTable';
import { useClientColumns } from '../components/ClientColumns';
import { useClients } from '../hooks/useClients';
import { useClientFilters } from '../hooks/useClientFilters';

export function ClientsPage() {
  const { filters, updateFilter, updateFilters, resetFilters } = useClientFilters();
  const { t } = useTranslation();
  const hasAccess = useAuthStore((state) => state.hasAccess);
  const navigate = useNavigate();

  const { data, isLoading } = useClients(filters);

  const handleSort = (field: string) => {
    if (filters.orderBy !== field) {
      updateFilters({ orderBy: field, order: 'asc' });
    } else {
      if (filters.order === 'asc') {
        updateFilters({ order: 'desc' });
      } else if (filters.order === 'desc') {
        updateFilters({ orderBy: undefined, order: undefined });
      } else {
        updateFilters({ order: 'asc' });
      }
    }
  };

  // Slot in the title row the table portals its Columns/Filters buttons into.
  const [tableControlsEl, setTableControlsEl] = React.useState<HTMLDivElement | null>(null);
  // Zoom is owned here so its control can sit in the Pagination row (left of
  // the per-page select) while the table applies the same value to its rows.
  const tableZoom = useTableZoom();

  const columns = useClientColumns({
    orderBy: filters.orderBy || '',
    order: filters.order || '',
    onSort: handleSort,
    filters,
  });

  return (
    <div>
      <div className="flex py-2 px-4 justify-between items-center border-b-1">
        <h1 className="text-xl font-semibold">
          {t('client')} & {t('contracts')}
        </h1>
        <div className="flex items-center gap-3">
          <div ref={setTableControlsEl} className="flex items-center gap-2" />
          {hasAccess('client', 'create') && (
            <>
              <Button onClick={() => navigate('actionPerson')}>
                {t('newPerson')}
              </Button>
              <Button onClick={() => navigate('actionCompany')}>
                {t('newCompany')}
              </Button>
            </>
          )}
        </div>
      </div>
      <div className="px-4 pt-4">
        <ClientsDataTable
          tableZoom={tableZoom}
          controlsContainer={tableControlsEl}
          data={data?.results || []}
          columns={columns}
          filters={filters}
          updateFilter={updateFilter}
          updateFilters={updateFilters}
          onClearFilters={resetFilters}
          isLoading={isLoading}
        />

        <Pagination
          leadingControls={<TableZoomControls zoom={tableZoom} />}
          page={filters.page}
          limit={filters.limit}
          total={data?.meta?.total ?? 0}
          onPageChange={(val) => updateFilter('page', val)}
          onLimitChange={(val) => updateFilters({ limit: val, page: 0 })}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
